<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { commissionDashboardApi } from 'dashboard/api/commissions';

const store = useStore();
const currentUser = computed(() => store.getters.getCurrentUser);

const loading = ref(true);
const data = ref(null);
const competence = ref(new Date().toISOString().slice(0, 7)); // YYYY-MM

// Últimas 12 competências para o seletor.
const competenceOptions = computed(() => {
  const out = [];
  const d = new Date();
  for (let i = 0; i < 12; i += 1) {
    const dt = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
});

const formatCurrency = v =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(parseFloat(v) || 0);

const formatPct = v => `${((parseFloat(v) || 0) * 100).toFixed(0)}%`;

const monthLabel = c => {
  const [y, m] = c.split('-');
  const meses = [
    'Jan',
    'Fev',
    'Mar',
    'Abr',
    'Mai',
    'Jun',
    'Jul',
    'Ago',
    'Set',
    'Out',
    'Nov',
    'Dez',
  ];
  return `${meses[parseInt(m, 10) - 1]}/${y}`;
};

function goalBarColor(pct) {
  if (pct >= 1) return '#22c55e';
  if (pct >= 0.8) return '#3b82f6';
  return '#ef4444';
}
function goalBadgeStyle(pct) {
  if (pct >= 1) return 'background:#dcfce7;color:#15803d';
  if (pct >= 0.8) return 'background:#dbeafe;color:#1d4ed8';
  return 'background:#fee2e2;color:#dc2626';
}

// Ritmo esperado: % do mês decorrido (só faz sentido p/ o mês vigente).
const paceMarkerPct = computed(() => {
  const today = new Date();
  if (competence.value !== today.toISOString().slice(0, 7)) return null;
  const day = today.getDate();
  const dim = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  return Math.round((day / dim) * 100);
});

const cm = computed(() => data.value?.current_month || {});
const goals = computed(() => data.value?.goals || {});
const history = computed(() => data.value?.history || {});
const rank = computed(() => data.value?.rank || {});
// Relação venda-a-venda que compõe a comissão da competência (vem do mesmo cálculo ao vivo
// que gera os totais, então a soma abaixo sempre fecha com "Comissão de vendas + Aporte").
const lancamentos = computed(() => data.value?.lancamentos || []);
const lancamentosTotal = computed(() =>
  lancamentos.value.reduce(
    (acc, l) => acc + (parseFloat(l.commission_amount) || 0),
    0
  )
);

const STATUS_STEPS = [
  { key: 'generated', label: 'A analisar', icon: 'i-lucide-clock' },
  { key: 'in_review', label: 'Auditada', icon: 'i-lucide-search-check' },
  { key: 'approved', label: 'Aprovada', icon: 'i-lucide-badge-check' },
  { key: 'paid', label: 'Recebida', icon: 'i-lucide-banknote' },
];
const STATUS_INDEX = {
  live: -1,
  draft: 0,
  generated: 0,
  in_review: 1,
  approved: 2,
  paid: 3,
};
const currentStatusIndex = computed(() => STATUS_INDEX[cm.value.status] ?? -1);

const statusBadgeClass = computed(() => {
  switch (cm.value.status) {
    case 'paid':
      return 'bg-emerald-500/15 text-emerald-600';
    case 'approved':
      return 'bg-blue-500/15 text-blue-600';
    case 'in_review':
      return 'bg-violet-500/15 text-violet-600';
    case 'generated':
      return 'bg-amber-500/15 text-amber-600';
    default:
      return 'bg-n-alpha-2 text-n-slate-11';
  }
});

const plusUnlocked = computed(() => cm.value.plus_unlocked);

async function fetchDashboard() {
  loading.value = true;
  try {
    const res = await commissionDashboardApi.show({
      competence: competence.value,
    });
    data.value = res.data;
  } catch {
    data.value = null;
  } finally {
    loading.value = false;
  }
}

onMounted(fetchDashboard);
</script>

<template>
  <div class="flex flex-col h-full overflow-auto bg-n-background">
    <div class="mx-auto w-full max-w-5xl p-4 sm:p-6 space-y-5">
      <!-- Header -->
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1
            class="flex items-center gap-2 text-xl font-bold text-n-slate-12"
          >
            <span class="i-lucide-wallet text-n-brand" />
            Minhas Comissões
          </h1>
          <p class="mt-0.5 text-sm text-n-slate-10">
            {{ currentUser.name }} · acompanhe seu desempenho e ganhos
          </p>
        </div>
        <select
          v-model="competence"
          class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none"
          @change="fetchDashboard"
        >
          <option v-for="c in competenceOptions" :key="c" :value="c">
            {{ monthLabel(c) }}
          </option>
        </select>
      </div>

      <div
        v-if="loading"
        class="flex items-center justify-center py-20 text-n-slate-10"
      >
        <span class="i-lucide-loader-2 animate-spin text-2xl" />
      </div>

      <template v-else-if="data">
        <!-- Hero: comissão do mês + status -->
        <div
          class="rounded-2xl border border-n-weak/40 bg-gradient-to-br from-emerald-500/10 to-n-alpha-1 p-5"
        >
          <div class="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p
                class="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-n-slate-10"
              >
                <span class="i-lucide-coins text-emerald-500" />
                Comissão do mês · {{ monthLabel(competence) }}
              </p>
              <p class="mt-1 text-4xl font-extrabold text-emerald-600">
                {{ formatCurrency(cm.total_final) }}
              </p>
              <p
                v-if="cm.official_total && cm.status !== 'live'"
                class="mt-0.5 text-xs text-n-slate-10"
              >
                Valor oficial do fechamento:
                <strong>{{ formatCurrency(cm.official_total) }}</strong>
              </p>
            </div>
            <span
              class="rounded-full px-3 py-1 text-xs font-semibold"
              :class="statusBadgeClass"
            >
              {{ cm.status_label }}
            </span>
          </div>

          <!-- Status timeline -->
          <div class="mt-5 flex items-center">
            <template v-for="(step, idx) in STATUS_STEPS" :key="step.key">
              <div class="flex flex-col items-center gap-1">
                <span
                  class="flex h-8 w-8 items-center justify-center rounded-full border-2 text-sm"
                  :class="
                    idx <= currentStatusIndex
                      ? 'border-emerald-500 bg-emerald-500 text-white'
                      : 'border-n-weak bg-n-solid-1 text-n-slate-9'
                  "
                >
                  <span :class="step.icon" />
                </span>
                <span
                  class="text-[10px] font-medium"
                  :class="
                    idx <= currentStatusIndex
                      ? 'text-emerald-600'
                      : 'text-n-slate-9'
                  "
                  >{{ step.label }}</span>
              </div>
              <div
                v-if="idx < STATUS_STEPS.length - 1"
                class="mx-1 mb-4 h-0.5 flex-1 rounded"
                :class="idx < currentStatusIndex ? 'bg-emerald-500' : 'bg-n-weak'"
              />
            </template>
          </div>
        </div>

        <!-- Cartões de totais históricos -->
        <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div class="rounded-xl border border-n-weak/40 bg-n-alpha-1 p-3">
            <p class="text-[10px] uppercase tracking-wider text-n-slate-10">
              Recebido (total)
            </p>
            <p class="mt-1 text-lg font-bold text-emerald-600">
              {{ formatCurrency(history.total_recebido) }}
            </p>
          </div>
          <div class="rounded-xl border border-n-weak/40 bg-n-alpha-1 p-3">
            <p class="text-[10px] uppercase tracking-wider text-n-slate-10">
              A receber
            </p>
            <p class="mt-1 text-lg font-bold text-blue-600">
              {{ formatCurrency(history.total_a_receber) }}
            </p>
          </div>
          <div class="rounded-xl border border-n-weak/40 bg-n-alpha-1 p-3">
            <p class="text-[10px] uppercase tracking-wider text-n-slate-10">
              Em apuração
            </p>
            <p class="mt-1 text-lg font-bold text-amber-600">
              {{ formatCurrency(history.total_em_apuracao) }}
            </p>
          </div>
          <div class="rounded-xl border border-n-weak/40 bg-n-alpha-1 p-3">
            <p class="text-[10px] uppercase tracking-wider text-n-slate-10">
              Total histórico
            </p>
            <p class="mt-1 text-lg font-bold text-n-slate-12">
              {{ formatCurrency(history.total_acumulado) }}
            </p>
          </div>
        </div>

        <!-- Metas -->
        <div
          class="space-y-5 rounded-xl border border-n-weak/40 bg-n-alpha-1 p-5"
        >
          <div class="flex items-center justify-between">
            <h3
              class="flex items-center gap-2 text-sm font-semibold text-n-slate-12"
            >
              <span class="i-lucide-target text-n-brand" />
              Minhas Metas — {{ monthLabel(competence) }}
            </h3>
            <span
              class="rounded-full px-2 py-0.5 text-xs font-bold"
              :style="goalBadgeStyle(parseFloat(cm.atingimento_total_pct))"
            >
              {{ formatPct(cm.atingimento_total_pct) }} do total
            </span>
          </div>

          <!-- Previdência -->
          <div class="space-y-2">
            <div class="flex items-center justify-between gap-3">
              <span
                class="flex items-center gap-2 text-sm font-semibold text-n-slate-12"
              >
                <span class="i-lucide-shield text-emerald-500" />
                Previdência
              </span>
              <span class="text-xs text-n-slate-11">
                {{ formatCurrency(cm.realizado_previdencia) }} /
                {{ formatCurrency(goals.previdencia) }}
              </span>
            </div>
            <div class="relative h-5 overflow-hidden rounded-full bg-slate-200">
              <div
                class="h-full rounded-full transition-all duration-500"
                :style="{
                  width:
                    Math.min(
                      (parseFloat(cm.atingimento_previdencia_pct) || 0) * 100,
                      100
                    ) + '%',
                  background: goalBarColor(
                    parseFloat(cm.atingimento_previdencia_pct) || 0
                  ),
                }"
              />
              <div
                v-if="paceMarkerPct !== null"
                :style="{
                  position: 'absolute',
                  top: '0',
                  bottom: '0',
                  left: paceMarkerPct + '%',
                  width: '2px',
                  background: '#f97316',
                  transform: 'translateX(-50%)',
                }"
              />
            </div>
            <p
              v-if="parseFloat(cm.falta_previdencia) > 0"
              class="text-[11px] text-n-slate-10"
            >
              Faltam
              <strong class="text-rose-600">{{
                formatCurrency(cm.falta_previdencia)
              }}</strong>
              para bater a meta de previdência.
            </p>
            <p v-else class="text-[11px] font-medium text-emerald-600">
              ✓ Meta de previdência batida!
            </p>
          </div>

          <!-- Risco -->
          <div class="space-y-2">
            <div class="flex items-center justify-between gap-3">
              <span
                class="flex items-center gap-2 text-sm font-semibold text-n-slate-12"
              >
                <span class="i-lucide-heart-pulse text-rose-500" />
                Risco
              </span>
              <span class="text-xs text-n-slate-11">
                {{ formatCurrency(cm.realizado_risco) }} /
                {{ formatCurrency(goals.risco) }}
              </span>
            </div>
            <div class="relative h-5 overflow-hidden rounded-full bg-slate-200">
              <div
                class="h-full rounded-full transition-all duration-500"
                :style="{
                  width:
                    Math.min(
                      (parseFloat(cm.atingimento_risco_pct) || 0) * 100,
                      100
                    ) + '%',
                  background: goalBarColor(
                    parseFloat(cm.atingimento_risco_pct) || 0
                  ),
                }"
              />
              <div
                v-if="paceMarkerPct !== null"
                :style="{
                  position: 'absolute',
                  top: '0',
                  bottom: '0',
                  left: paceMarkerPct + '%',
                  width: '2px',
                  background: '#f97316',
                  transform: 'translateX(-50%)',
                }"
              />
            </div>
            <p
              v-if="parseFloat(cm.falta_risco) > 0"
              class="text-[11px] text-n-slate-10"
            >
              Faltam
              <strong class="text-rose-600">{{
                formatCurrency(cm.falta_risco)
              }}</strong>
              para bater a meta de risco.
            </p>
            <p v-else class="text-[11px] font-medium text-emerald-600">
              ✓ Meta de risco batida!
            </p>
          </div>

          <div
            v-if="paceMarkerPct !== null"
            class="flex items-center gap-2 text-[11px] text-n-slate-10"
          >
            <span
              class="inline-block w-3 shrink-0"
              style="height: 3px; background: #f97316; border-radius: 2px"
            />
            <span
              >Linha laranja = ritmo esperado hoje ({{ paceMarkerPct }}%). Barra
              além da linha = à frente do ritmo.</span>
          </div>
        </div>

        <!-- Premiação Plus -->
        <div
          class="rounded-xl border p-4"
          :class="
            plusUnlocked
              ? 'border-emerald-500/30 bg-emerald-500/5'
              : 'border-amber-500/30 bg-amber-500/5'
          "
        >
          <div class="flex items-center justify-between gap-3">
            <span
              class="flex items-center gap-2 text-sm font-semibold"
              :class="plusUnlocked ? 'text-emerald-700' : 'text-amber-700'"
            >
              <span class="i-lucide-star" />
              Premiação Plus
            </span>
            <span class="text-lg font-bold" :class="plusUnlocked ? 'text-emerald-600' : 'text-n-slate-10'">
              {{ formatCurrency(cm.premiacao_plus) }}
            </span>
          </div>
          <p
            v-if="plusUnlocked"
            class="mt-1 text-[11px] font-medium text-emerald-700"
          >
            ✓ Desbloqueada — você bateu previdência E risco.
          </p>
          <p v-else class="mt-1 text-[11px] text-amber-800">
            🔒 Bata as DUAS metas (previdência e risco) para liberar a Premiação
            Plus.
            <span v-if="parseFloat(cm.falta_previdencia) > 0"
              >Falta {{ formatCurrency(cm.falta_previdencia) }} em
              previdência.</span>
            <span v-if="parseFloat(cm.falta_risco) > 0"
              >Falta {{ formatCurrency(cm.falta_risco) }} em risco.</span>
          </p>
        </div>

        <!-- Composição da comissão do mês -->
        <div class="rounded-xl border border-n-weak/40 bg-n-alpha-1 p-5">
          <h3
            class="mb-3 flex items-center gap-2 text-sm font-semibold text-n-slate-12"
          >
            <span class="i-lucide-receipt text-n-brand" />
            Como sua comissão é formada
          </h3>
          <div class="space-y-2 text-sm">
            <div class="flex items-center justify-between">
              <span class="text-n-slate-11"
                >Comissão de vendas ({{ cm.qtd_vendas }})</span>
              <span class="font-medium text-n-slate-12">{{
                formatCurrency(cm.comissao_vendas)
              }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-n-slate-11"
                >Aporte / Portabilidade ({{ cm.qtd_aporte }})
                <span class="text-[10px] text-n-slate-9"
                  >· 1% no fechamento</span
                ></span>
              <span class="font-medium text-n-slate-12">{{
                formatCurrency(cm.comissao_aporte)
              }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-n-slate-11">Premiação Volume</span>
              <span class="font-medium text-n-slate-12">{{
                formatCurrency(cm.premiacao_volume)
              }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-n-slate-11">Premiação Plus</span>
              <span class="font-medium text-n-slate-12">{{
                formatCurrency(cm.premiacao_plus)
              }}</span>
            </div>
            <div
              class="flex items-center justify-between border-t border-n-weak/40 pt-2 text-base"
            >
              <span class="font-semibold text-n-slate-12">Total</span>
              <span class="font-bold text-emerald-600">{{
                formatCurrency(cm.total_final)
              }}</span>
            </div>
          </div>
        </div>

        <!-- Relação venda-a-venda que compõe a comissão do mês -->
        <div class="rounded-xl border border-n-weak/40 bg-n-alpha-1 p-5">
          <h3
            class="mb-1 flex items-center gap-2 text-sm font-semibold text-n-slate-12"
          >
            <span class="i-lucide-list-checks text-n-brand" />
            Minhas vendas do mês ({{ lancamentos.length }})
          </h3>
          <p class="mb-3 text-xs text-n-slate-10">
            Cada venda que entrou na sua comissão desta competência, com a base
            de cálculo e o percentual aplicado.
          </p>

          <div v-if="lancamentos.length" class="overflow-x-auto">
            <table class="w-full text-left text-sm">
              <thead>
                <tr class="border-b border-n-weak/40 text-[11px] uppercase text-n-slate-10">
                  <th class="py-2 pr-3 font-semibold">Data</th>
                  <th class="py-2 pr-3 font-semibold">Cliente</th>
                  <th class="py-2 pr-3 font-semibold">Tipo</th>
                  <th class="py-2 pr-3 text-right font-semibold">Previdência</th>
                  <th class="py-2 pr-3 text-right font-semibold">Risco</th>
                  <th class="py-2 pr-3 text-right font-semibold">Base</th>
                  <th class="py-2 pr-3 text-right font-semibold">%</th>
                  <th class="py-2 text-right font-semibold">Comissão</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="l in lancamentos"
                  :key="l.id"
                  class="border-b border-n-weak/20 last:border-0"
                >
                  <td class="py-2 pr-3 whitespace-nowrap text-n-slate-11">
                    {{ l.sale_date || '—' }}
                  </td>
                  <td class="py-2 pr-3 font-medium text-n-slate-12">
                    {{ l.participante }}
                  </td>
                  <td class="py-2 pr-3">
                    <span
                      class="rounded px-1.5 py-0.5 text-[10px] font-semibold"
                      :class="
                        l.is_aporte
                          ? 'bg-amber-500/15 text-amber-700'
                          : 'bg-n-brand/15 text-n-brand'
                      "
                    >
                      {{ l.sale_class_label }}
                    </span>
                  </td>
                  <td class="py-2 pr-3 text-right tabular-nums text-n-slate-11">
                    {{ formatCurrency(l.val_previdencia) }}
                  </td>
                  <td class="py-2 pr-3 text-right tabular-nums text-n-slate-11">
                    {{ formatCurrency(l.val_risco) }}
                  </td>
                  <td class="py-2 pr-3 text-right tabular-nums text-n-slate-11">
                    {{ formatCurrency(l.commission_base) }}
                  </td>
                  <td class="py-2 pr-3 text-right tabular-nums text-n-slate-11">
                    {{ formatPct(l.commission_pct) }}
                  </td>
                  <td
                    class="py-2 text-right font-semibold tabular-nums text-emerald-600"
                  >
                    {{ formatCurrency(l.commission_amount) }}
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="border-t border-n-weak/40">
                  <td colspan="7" class="py-2 pr-3 text-right font-semibold text-n-slate-12">
                    Soma das vendas
                  </td>
                  <td class="py-2 text-right font-bold tabular-nums text-emerald-600">
                    {{ formatCurrency(lancamentosTotal) }}
                  </td>
                </tr>
              </tfoot>
            </table>
            <p class="mt-3 text-[11px] text-n-slate-9">
              As premiações Volume e Plus são apuradas sobre o conjunto do mês e
              entram somadas no total, por isso não aparecem em nenhuma linha
              individual. Aporte/Portabilidade é apurado por CPF no fechamento.
            </p>
          </div>
          <p v-else class="py-4 text-center text-sm text-n-slate-10">
            Nenhuma venda registrada nesta competência ainda.
          </p>
        </div>

        <!-- Ranking + Histórico -->
        <div class="grid gap-4 sm:grid-cols-2">
          <div
            class="flex flex-col items-center justify-center rounded-xl border border-n-weak/40 bg-n-alpha-1 p-5 text-center"
          >
            <span class="i-lucide-trophy text-3xl text-amber-500" />
            <p class="mt-2 text-[11px] uppercase tracking-wider text-n-slate-10">
              Sua posição no ranking · {{ monthLabel(competence) }}
            </p>
            <p v-if="rank.position" class="text-3xl font-extrabold text-n-slate-12">
              {{ rank.position }}º
              <span class="text-base font-medium text-n-slate-10"
                >de {{ rank.total }}</span>
            </p>
            <p v-else class="mt-1 text-sm text-n-slate-10">
              Sem vendas neste mês ainda
            </p>
          </div>

          <div class="rounded-xl border border-n-weak/40 bg-n-alpha-1 p-5">
            <h3
              class="mb-2 flex items-center gap-2 text-sm font-semibold text-n-slate-12"
            >
              <span class="i-lucide-history text-n-brand" />
              Histórico mensal
            </h3>
            <div
              v-if="history.monthly && history.monthly.length"
              class="space-y-1.5 max-h-44 overflow-auto"
            >
              <div
                v-for="m in history.monthly"
                :key="m.competence"
                class="flex items-center justify-between gap-2 text-sm"
              >
                <span class="text-n-slate-11">{{
                  monthLabel(m.competence)
                }}</span>
                <span class="flex items-center gap-2">
                  <span
                    class="rounded-full px-1.5 py-0.5 text-[9px] font-semibold"
                    :class="
                      m.status === 'paid'
                        ? 'bg-emerald-500/15 text-emerald-600'
                        : m.status === 'approved'
                          ? 'bg-blue-500/15 text-blue-600'
                          : 'bg-amber-500/15 text-amber-600'
                    "
                    >{{ m.status_label }}</span>
                  <span class="font-semibold text-n-slate-12">{{
                    formatCurrency(m.total_final)
                  }}</span>
                </span>
              </div>
            </div>
            <p v-else class="text-xs text-n-slate-10">
              Sem fechamentos anteriores.
            </p>
          </div>
        </div>
      </template>

      <div v-else class="py-20 text-center text-n-slate-10">
        Não foi possível carregar suas comissões.
      </div>
    </div>
  </div>
</template>
